import { assessmentReducer } from "@/features/assessments/assessments.slice";
import { protocolQuestionEditMiddleware, protocolQuestionEditReducer } from "@/features/protocol_questions/store";
import { authReducer } from "@/libs/auth";
import { featureFlagsReducer } from "@/libs/feature-flags";
import { reducer as menuReducer } from "@/ui-components/layouts/components/Menu/menu-slice";
import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { createReduxEnhancer } from "@sentry/react";
import { createBrowserHistory } from "history";
import { useDispatch, useSelector, useStore } from "react-redux";
import { createReduxHistoryContext } from "redux-first-history";
import { api } from "./api";

const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: createBrowserHistory(),
});

const rootReducer = combineReducers({
  featureFlags: featureFlagsReducer,
  auth: authReducer,
  assessment: assessmentReducer,
  router: routerReducer,
  menu: menuReducer,
  protocolQuestionEdit: protocolQuestionEditReducer,
  [api.reducerPath]: api.reducer,
});

export type RootState = ReturnType<typeof rootReducer>;

export function setupStore(preloadedState?: Partial<RootState>) {
  const store = configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware().concat([api.middleware, routerMiddleware, protocolQuestionEditMiddleware]),
    enhancers: getDefaultEnhancers => {
      return getDefaultEnhancers().concat(createReduxEnhancer({ attachReduxState: true }));
    },
  });

  setupListeners(store.dispatch);

  return store;
}

export const store = setupStore();
export const history = createReduxHistory(store);

export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore["dispatch"];

export const selectState = (state: RootState) => state;

export const useAppStore = () => {
  return useStore<RootState>();
};
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
